// ============================================================================
// crmCaixaAtiva · a Caixa do CRM está à vista e com foco? (30/10/2026, CRM_VIGIA)
// ============================================================================
// Pedido do dono (30/10/2026): "notificação de mensagem para quando a recepcionista
// estiver ... em outra aba do DNA e não tiver no CRM". O vigia (VigiaCrm, em todas as
// telas) fica quieto quando a Caixa do CRM está montada, visível e com foco: ali quem
// toca é a própria Caixa (ela já lê na hora e mostra a fala nova). Fora disso, avisa ele.
// A aba líder do vigia pode ser outra aba do navegador, então o estado vale ENTRE abas:
//   · a CaixaEntrada chama marcarCaixaAtiva() enquanto está montada (visão Agora, sem
//     filtro de unidade: a que ouve todas as conversas da pessoa) e o desmarcar ao sair;
//   · a cada foco, perda de foco, troca de visibilidade e a cada 15 s, a aba grava a
//     marca dela { [aba]: { ativa, em } } em CHAVE_CAIXA_ATIVA (uma por aba: duas Caixas
//     abertas não apagam uma a outra); ao sair (ou fechar), tira a dela;
//   · caixaAtivaAgora(): esta aba (montada, visível e com foco) ou a marca de outra aba
//     com ativa = true e de menos de 45 s (uma aba que travou ou fechou sem avisar não
//     segura o vigia).
// Sem localStorage: vale só esta aba. Nada aqui lança para a tela.
// 30/10/2026 (revisão): a chave é a do contrato (crmAvisoContrato.js registra todas as do vigia
// para ninguém colidir), reexportada com o mesmo nome. O vigia também consulta caixaAtivaAgora()
// antes de deixar o aviso do canal ao vivo antecipar uma leitura (a Caixa à vista já lê na hora).
// ============================================================================

import { CHAVE_CAIXA_ATIVA } from './crmAvisoContrato.js';

export { CHAVE_CAIXA_ATIVA };
export const BATIDA_CAIXA_MS = 15_000;
export const VALIDADE_CAIXA_MS = 45_000;

const ABA = `c${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;
let montadas = 0;

function armazem() {
  try { return typeof window !== 'undefined' && window.localStorage ? window.localStorage : null; } catch { return null; }
}
const docPadrao = () => (typeof document !== 'undefined' ? document : null);
const winPadrao = () => (typeof window !== 'undefined' ? window : null);

/** A aba está visível e com o foco do sistema (a pessoa está olhando para ela agora). */
export function abaComFoco(doc = docPadrao()) {
  try {
    if (!doc) return false;
    if (doc.visibilityState === 'hidden') return false;
    return typeof doc.hasFocus === 'function' ? doc.hasFocus() === true : false;
  } catch { return false; }
}

/** As marcas de todas as abas (as velhas saem). */
function lerMarcas(storage, agoraMs) {
  try {
    const bruto = storage ? storage.getItem(CHAVE_CAIXA_ATIVA) : null;
    const m = bruto ? JSON.parse(bruto) : null;
    if (!m || typeof m !== 'object' || Array.isArray(m)) return {};
    const out = {};
    for (const [aba, v] of Object.entries(m)) {
      const em = Number(v?.em);
      if (v && typeof v === 'object' && Number.isFinite(em) && Math.abs(agoraMs - em) < VALIDADE_CAIXA_MS) out[aba] = { ativa: v.ativa === true, em };
    }
    return out;
  } catch { return {}; }
}

/**
 * A Caixa (visão Agora, sem filtro de unidade) está montada nesta aba. → desmarcar()
 * relogio só nos testes ({ agora, setInterval, clearInterval }).
 */
export function marcarCaixaAtiva({ doc = docPadrao(), win = winPadrao(), storage = armazem(), relogio = null, aba = ABA } = {}) {
  const agora = () => (relogio ? relogio.agora() : Date.now());
  montadas += 1;
  const escrever = (fn) => {
    if (!storage) return;
    try {
      const m = fn(lerMarcas(storage, agora()));
      if (Object.keys(m).length) storage.setItem(CHAVE_CAIXA_ATIVA, JSON.stringify(m));
      else storage.removeItem(CHAVE_CAIXA_ATIVA);
    } catch { /* sem armazenamento: vale esta aba */ }
  };
  const gravar = () => escrever(m => ({ ...m, [aba]: { ativa: abaComFoco(doc), em: agora() } }));
  const apagarSeMinha = () => escrever(m => { const { [aba]: _minha, ...resto } = m; void _minha; return resto; });
  gravar();
  win?.addEventListener?.('focus', gravar);
  win?.addEventListener?.('blur', gravar);
  win?.addEventListener?.('pagehide', apagarSeMinha);
  doc?.addEventListener?.('visibilitychange', gravar);
  const si = relogio?.setInterval || ((f, ms) => setInterval(f, ms));
  const ci = relogio?.clearInterval || ((t) => clearInterval(t));
  const t = si(gravar, BATIDA_CAIXA_MS);
  let feito = false;
  return () => {
    if (feito) return;
    feito = true;
    montadas = Math.max(0, montadas - 1);
    ci(t);
    win?.removeEventListener?.('focus', gravar);
    win?.removeEventListener?.('blur', gravar);
    win?.removeEventListener?.('pagehide', apagarSeMinha);
    doc?.removeEventListener?.('visibilitychange', gravar);
    apagarSeMinha();
  };
}

/** A Caixa está à vista e com foco: nesta aba ou em outra deste navegador. */
export function caixaAtivaAgora({ doc = docPadrao(), storage = armazem(), agoraMs = Date.now() } = {}) {
  if (montadas > 0 && abaComFoco(doc)) return true;
  return Object.values(lerMarcas(storage, agoraMs)).some(v => v.ativa === true);
}

/** Só para os testes. */
export function _zerarCaixaAtiva() { montadas = 0; }
